import React, { useState, useEffect, useRef } from 'react';
import { Table, Tag, Button, Space, Tooltip, Empty, message, Badge, Progress, Modal, Form, Input, Select, Popconfirm } from 'antd';
import { 
  PlayCircleOutlined, 
  StopOutlined, 
  FileTextOutlined,
  CalendarOutlined,
  PhoneOutlined,
  CopyOutlined,
  CarOutlined,
  FieldNumberOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const API_URL = process.env.REACT_APP_API_URL || '';

// Progress calculation constants (same as LogModal)
const PROGRESS_THRESHOLDS = {
  MIN_LOGS_SUCCESS: 142,
  MAX_LOGS_SUCCESS: 153,
  EXPECTED_LOGS: 147.5,
  EARLY_STAGE: 30,
  MID_STAGE: 75,
  LATE_STAGE: 120,
};

// Sound notification utility - works cross-platform (Mac/Windows)
const playNotificationSound = (type = 'success') => {
  try {
    // Create audio context for cross-platform compatibility
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const duration = type === 'success' ? 0.3 : 0.5;
    const frequencies = type === 'success' 
      ? [523.25, 659.25, 783.99] // Success: C5, E5, G5 (major chord)
      : [349.23, 293.66]; // Error: F4, D4 (minor interval)
    
    frequencies.forEach((frequency, index) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
      oscillator.type = 'sine';
      
      // Envelope for smooth sound
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 0.05);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + duration);
      
      const startTime = audioContext.currentTime + (index * 0.1);
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    });
    
    console.log(`🔊 Played ${type} notification sound`);
  } catch (error) {
    console.warn('Could not play notification sound:', error);
    // Fallback - just log if Web Audio API fails
    console.log(`🔇 Audio playback not available, but test ${type} notification triggered`);
  }
};

function ExecutionDashboard({ executions, onStartExecution, onStopExecution, onStartAll, onShowLogs, onTableChange, loading, pagination, sorting, filters, onUpdateExecution, onDeleteExecution }) {
  const [executionData, setExecutionData] = useState({}); // Store both counts and progress
  const previousExecutionsRef = useRef({}); // Track previous execution states for change detection
  
  // Edit modal state
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingExecution, setEditingExecution] = useState(null);
  const [editForm] = Form.useForm();

  // Calculate progress based on log count
  const calculateProgress = (logCount) => {
    if (logCount === 0) return { percent: 0, status: 'normal', stage: 'Not Started' };
    
    const percent = Math.min(100, (logCount / PROGRESS_THRESHOLDS.EXPECTED_LOGS) * 100);
    
    let status = 'active';
    let stage = '';
    
    if (logCount <= PROGRESS_THRESHOLDS.EARLY_STAGE) {
      stage = 'Initializing';
    } else if (logCount <= PROGRESS_THRESHOLDS.MID_STAGE) {
      stage = 'Testing';
    } else if (logCount <= PROGRESS_THRESHOLDS.LATE_STAGE) {
      stage = 'Processing';
    } else if (logCount >= PROGRESS_THRESHOLDS.MIN_LOGS_SUCCESS && 
               logCount <= PROGRESS_THRESHOLDS.MAX_LOGS_SUCCESS) {
      stage = 'Completing';
      status = 'success';
    } else if (logCount > PROGRESS_THRESHOLDS.MAX_LOGS_SUCCESS) {
      stage = 'Extended Run';
      status = 'exception';
    } else {
      stage = 'Finalizing';
    }
    
    return { percent: Math.round(percent), status, stage };
  };

  // Fetch log count and progress for a specific execution
  const fetchLogData = async (executionId) => {
    try {
      const response = await fetch(`${API_URL}/api/logs/${executionId}`);
      const data = await response.json();
      return {
        count: data.totalLogs || 0,
        progress: data.progress || calculateProgress(data.totalLogs || 0)
      };
    } catch (error) {
      console.error('Failed to fetch log data for', executionId, error);
      return {
        count: 0,
        progress: calculateProgress(0)
      };
    }
  };

  // Update log data for running executions
  const updateExecutionData = async () => {
    const runningExecutions = executions.filter(exec => exec.status === 'running');
    if (runningExecutions.length === 0) return;

    const updates = {};
    await Promise.all(
      runningExecutions.map(async (exec) => {
        const logData = await fetchLogData(exec.id);
        updates[exec.id] = logData;
      })
    );

    setExecutionData(prev => ({ ...prev, ...updates }));
  };

  // Monitor execution status changes and play notification sounds
  useEffect(() => {
    if (!executions || executions.length === 0) return;

    // Check for status changes from previous executions
    executions.forEach(execution => {
      const previousStatus = previousExecutionsRef.current[execution.id]?.status;
      const currentStatus = execution.status;
      
      // Only trigger notification if status actually changed
      if (previousStatus && previousStatus !== currentStatus) {
        // Trigger sound notification for completion states
        if (currentStatus === 'completed' || currentStatus === 'success') {
          playNotificationSound('success');
          console.log(`🎉 Execution ${execution.id} completed successfully!`);
          message.success({
            content: `Test completed for ${execution.plate}! 🎉`,
            duration: 4,
            style: { fontWeight: 'bold' }
          });
        } else if (currentStatus === 'failed') {
          playNotificationSound('error');
          console.log(`💥 Execution ${execution.id} failed!`);
          message.error({
            content: `Test failed for ${execution.plate} ❌`,
            duration: 4,
            style: { fontWeight: 'bold' }
          });
        } else if (currentStatus === 'stopped') {
          playNotificationSound('error');
          console.log(`⏹️ Execution ${execution.id} was stopped!`);
          message.warning({
            content: `Test stopped for ${execution.plate} ⏹️`,
            duration: 3,
            style: { fontWeight: 'bold' }
          });
        }
      }

      // Update previous state
      previousExecutionsRef.current[execution.id] = {
        status: currentStatus,
        plate: execution.plate
      };
    });
  }, [executions]);

  // Update execution data periodically for running executions
  useEffect(() => {
    updateExecutionData(); // Initial fetch

    const interval = setInterval(() => {
      updateExecutionData();
    }, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, [executions]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'default';
      case 'running': 
      // case 'started': return 'processing'; // Removed - using 'running' status instead
      case 'completed': 
      case 'success': return 'success';
      case 'failed': return 'error';
      case 'stopped': return 'warning';
      default: return 'default';
    }
  };

    // Copy policy info to clipboard
    const handleCopyPolicy = (plate, policyNo) => {
      const text = `${policyNo}`;
      navigator.clipboard.writeText(text).then(() => {
        message.success('Copied!');
      });
    };

    // Handle edit execution
    const handleEditExecution = (execution) => {
      setEditingExecution(execution);
      editForm.setFieldsValue({
        plate: execution.plate,
        req_number: execution.req_number,
        req_date: execution.req_date,
        policy_no: execution.policy_no,
        type: execution.type,
        phone_number: execution.phone_number
      });
      setEditModalVisible(true);
    };

    // Handle save edit
    const handleSaveEdit = async () => {
      try {
        const values = await editForm.validateFields();
        const updatedExecution = {
          ...editingExecution,
          ...values
        };
        
        if (onUpdateExecution) {
          await onUpdateExecution(editingExecution.id, updatedExecution);
          message.success('Execution updated successfully!');
          setEditModalVisible(false);
          setEditingExecution(null);
          editForm.resetFields();
        }
      } catch (error) {
        console.error('Failed to update execution:', error);
        message.error('Failed to update execution');
      }
    };

    // Handle delete execution
    const handleDeleteExecution = async (executionId) => {
      try {
        if (onDeleteExecution) {
          await onDeleteExecution(executionId);
          message.success('Execution deleted successfully!');
        }
      } catch (error) {
        console.error('Failed to delete execution:', error);
        message.error('Failed to delete execution');
      }
    };

    // Handle cancel edit
    const handleCancelEdit = () => {
      setEditModalVisible(false);
      setEditingExecution(null);
      editForm.resetFields();
    };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '⏳ ';
      case 'running': 
      // case 'started': return '🔄 '; // Removed - using 'running' status instead
      case 'completed': 
      case 'success': return '✅ ';
      case 'failed': return '❌ ';
      case 'stopped': return '⏹️ ';
      default: return '❓ ';
    }
  };

  const formatDuration = (startTime, endTime) => {
    // Note: started_at and completed_at columns may not exist in the table
    // For now, show duration based on created_at if running
    if (!startTime) return '-';
    
    const start = dayjs(startTime);
    const end = endTime ? dayjs(endTime) : dayjs();
    const duration = end.diff(start, 'second');
    
    if (duration < 60) return `${duration}s`;
    if (duration < 3600) return `${Math.floor(duration / 60)}m ${duration % 60}s`;
    return `${Math.floor(duration / 3600)}h ${Math.floor((duration % 3600) / 60)}m`;
  };

  const columns = [
    {
      title: 'Plate',
      dataIndex: 'plate',
      key: 'plate',
      width: 100,
      sorter: true,
      sortOrder: sorting.field === 'plate' ? (sorting.order === 'asc' ? 'ascend' : 'descend') : null,
      render: (plate) => (
        <Space>
          <CarOutlined style={{ color: '#1890ff' }} />
          <span style={{ fontWeight: 'bold' }}>{plate}</span>
        </Space>
      ),
    },
    {
      title: 'Request #',
      dataIndex: 'req_number',
      key: 'req_number',
      width: 120,
      render: (reqNumber) => (
        <span style={{ fontFamily: 'monospace', color: '#666' }}>#{reqNumber}</span>
      ),
    },
    /*
    {
      title: 'Phone',
      dataIndex: 'phone_number',
      key: 'phone_number',
      width: 140,
      render: (phone) => (
        <Space>
          <PhoneOutlined style={{ color: '#52c41a' }} />
          <span style={{ fontFamily: 'monospace' }}>{phone}</span>
        </Space>
      ),
    },
    */
    
    {
      title: 'Req Date',
      dataIndex: 'req_date',
      key: 'req_date',
      width: 120,
      render: (date) => (
        <Space>
          <span>{date}</span>
        </Space>
      ),
    },
    {
      title: 'Policy Number',
      dataIndex: 'policy_no',
      key: 'policy_no',
      width: 100,
      render: (policy_no, record) => (
        <Space>
          {policy_no && (
            <Tooltip title="Kopyala">
              <Button
                size="small"
                type="text"
                onClick={() => handleCopyPolicy(record.plate, policy_no)}
                variant='dashed'
                color='blue'
              >
                {policy_no}
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Policy Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type) => (
        <Tag color={type === 'new' ? 'green' : 'blue'}>{type}</Tag>
      ),
    },
    /*
    {
      title: 'Test Type',
      dataIndex: 'test_type',
      key: 'test_type',
      width: 100,
      render: (testType) => {
        // Default to 'run' if test_type column doesn't exist or is null
        const actualType = testType || 'run';
        const typeMap = {
          'run': { color: 'blue', label: 'Run' },
          'headed': { color: 'green', label: 'Headed' },
          'ui': { color: 'purple', label: 'UI' },
          'setup': { color: 'orange', label: 'Setup' },
          'clear-auth': { color: 'red', label: 'Clear Auth' }
        };
        const type = typeMap[actualType] || { color: 'default', label: actualType };
        return <Tag color={type.color}>{type.label}</Tag>;
      },
    },
    */
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      filters: [
        { text: 'All', value: 'all' },
        { text: 'Pending', value: 'pending' },
        { text: 'Running', value: 'running' },
        // { text: 'Started', value: 'started' }, // Removed - using 'running' status instead
        { text: 'Completed', value: 'completed' },
        { text: 'Success', value: 'success' },
        { text: 'Failed', value: 'failed' },
        { text: 'Stopped', value: 'stopped' }
      ],
      filteredValue: filters.status !== 'all' ? [filters.status] : null,
      sorter: true,
      sortOrder: sorting.field === 'status' ? (sorting.order === 'asc' ? 'ascend' : 'descend') : null,
      render: (status) => {
        // Handle null or undefined status
        const actualStatus = status || 'pending';
        // Normalize old status values to new ones for display
        const displayStatus = 
                             actualStatus === 'success' ? 'completed' : actualStatus;
        return (
          <span style={{ textTransform: 'capitalize' }}>
            {getStatusIcon(actualStatus)} {displayStatus}
          </span>
        );
      },
    },
    {
      title: 'Duration / Progress',
      key: 'duration',
      width: 160,
      render: (_, record) => {
        // Show progress for running tests
        if (record.status === 'running') {
          const execData = executionData[record.id];
          const logCount = execData?.count || 0;
          const progress = execData?.progress || calculateProgress(logCount);

          return (
            <div style={{ width: '80%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', color: '#666' }}>
                  {progress.stage}
                </span>
              </div>
              <Progress 
                percent={progress.percent} 
                status={progress.status}
                size="small"
                showInfo={true}
              />
            </div>
          );
        }

        // Show duration for completed/failed/stopped tests
        if (record.status === 'completed' || record.status === 'failed' || record.status === 'stopped') {
          const startTime = record.started_at || record.created_at;
          const endTime = record.completed_at || record.updated_at || record.created_at;
          
          if (startTime && endTime && startTime !== endTime) {
            return (
              <Tag>
                {formatDuration(startTime, endTime)}
              </Tag>
            );
          }
        }

        // Default case for pending or other states
        return (
          <span style={{ color: '#999', fontSize: '12px' }}>
            {record.status === 'pending' ? 'Waiting...' : '-'}
          </span>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 100,
      sorter: true,
      sortOrder: sorting.field === 'created_at' ? (sorting.order === 'asc' ? 'ascend' : 'descend') : null,
      render: (date, record) => (
        <Tooltip title={dayjs(date).format('DD MMM YYYY - HH:mm:ss') + ' - ' + record.status}>
          <span>
            {dayjs(date).format('MMM DD - HH:mm')}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space size="small">
          {record.status === 'pending' && (
            <Tooltip title="Start execution">
              <Button
                type="primary"
                size="small"
                icon={<PlayCircleOutlined />}
                onClick={() => onStartExecution(record.id)}
                style={{ 
                  backgroundColor: '#52c41a',
                  borderColor: '#52c41a'
                }}
              >
                Start
              </Button>
            </Tooltip>
          )}
          
          {record.status === 'running' && (
            <Tooltip title="Stop execution">
              <Button
                danger
                size="small"
                icon={<StopOutlined />}
                onClick={() => onStopExecution(record.id)}
              >
                Stop
              </Button>
            </Tooltip>
          )}
          
          {(record.status === 'failed' || record.status === 'stopped' || record.type === 'Teklif') && (
            <Tooltip title="Try again">
              <Button
                type="primary"
                size="small"
                icon={<PlayCircleOutlined />}
                onClick={() => onStartExecution(record.id)}
                style={{ 
                  backgroundColor: '#faad14',
                  borderColor: '#faad14'
                }}
              >
                Retry
              </Button>
            </Tooltip>
          )}
          
          <Tooltip title="View logs">
            <Button
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => onShowLogs(record.id)}
              disabled={!record.id}
            >
              Logs
            </Button>
          </Tooltip>
          
          {/* Edit button - only show for pending executions */}
          {record.status !== 'completed' && (
            <Tooltip title="Edit execution">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditExecution(record)}
                type="text"
              />
            </Tooltip>
          )}
          
          {/* Delete button - only show for pending, failed, stopped executions */}
          {(record.status === 'pending' || record.status === 'failed' || record.status === 'stopped') && (
            <Popconfirm
              title="Are you sure you want to delete this execution?"
              onConfirm={() => handleDeleteExecution(record.id)}
              okText="Yes"
              cancelText="No"
              placement="topRight"
            >
              <Tooltip title="Delete execution">
                <Button
                  size="small"
                  icon={<DeleteOutlined />}
                  danger
                  type="text"
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  if (executions.length === 0 && !loading) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <div>
            <p>No executions found</p>
            <small style={{ color: '#666' }}>Create your first execution to get started</small>
          </div>
        }
      />
    );
  }

  // Count pending executions for Start All button
  const pendingExecutions = executions.filter(exec => exec.status === 'pending');
  const failedStoppedExecutions = executions.filter(exec => exec.status === 'failed' || exec.status === 'stopped');

  return (
    <div>
      {/* Start All / Retry All Buttons */}
      {(pendingExecutions.length > 0 || failedStoppedExecutions.length > 0) && (
        <div style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
          {pendingExecutions.length > 0 && (
            <Button
              type="primary"
              size="large"
              icon={<PlayCircleOutlined />}
              onClick={() => onStartAll(pendingExecutions.map(exec => exec.id))}
              style={{
                backgroundColor: '#52c41a',
                borderColor: '#52c41a',
                fontWeight: 'bold'
              }}
            >
              🚀 Start All ({pendingExecutions.length})
            </Button>
          )}
          
        </div>
      )}
      
      <Table
      columns={columns}
      dataSource={executions}
      rowKey="id"
      loading={loading}
      onChange={onTableChange}
      pagination={{
        current: pagination.page,
        pageSize: pagination.pageSize,
        total: pagination.total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        position: ['bottomRight'],
        size: 'small',
        pageSizeOptions: ['10', '20', '50', '100']
      }}
      scroll={{ x: 1000 }}
      size="middle"
      style={{ 
        backgroundColor: 'white',
        borderRadius: '6px'
      }}
      rowClassName={(record) => {
        switch (record.status) {
          case 'running': 
          // case 'started': return 'row-running'; // Removed - using 'running' status instead
          case 'completed': 
          case 'success': return 'row-completed';
          case 'failed': return 'row-failed';
          case 'stopped': return 'row-stopped';
          default: return '';
        }
      }}
    />
    
    {/* Edit Modal */}
    <Modal
      title="Edit Execution"
      open={editModalVisible}
      onOk={handleSaveEdit}
      onCancel={handleCancelEdit}
      okText="Save"
      cancelText="Cancel"
      width={600}
    >
      <Form
        form={editForm}
        layout="vertical"
        initialValues={editingExecution}
      >
        <Form.Item
          label="License Plate"
          name="plate"
          rules={[{ required: true, message: 'License plate is required' }]}
        >
          <Input placeholder="e.g., ABC123" style={{ textTransform: 'uppercase' }} />
        </Form.Item>
        
        <Form.Item
          label="Request Number"
          name="req_number"
          rules={[{ required: true, message: 'Request number is required' }]}
        >
          <Input placeholder="Request number" />
        </Form.Item>
        
        <Form.Item
          label="Request Date"
          name="req_date"
          rules={[{ required: true, message: 'Request date is required' }]}
        >
          <Input placeholder="YYYY-MM-DD" />
        </Form.Item>
        
      </Form>
    </Modal>
    
    </div>
  );
}

export default ExecutionDashboard;